/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useAuth, isGuestUser } from "../../../../context/AuthContext";
import { formatCurrency } from "../../../../utils/format";
import { formatCurrency as utilFormatCurrency } from "../../../../utils/format";
import { computeTotals } from "../utils/calculations";
import {
  isOriginationRow,
  calcOriginationDollarValue,
  getTemplateOriginationPercent,
  isDeliberateManualEdit,
} from "../../../../utils/originationFee";
import {
  hasAutoEstimatableLoanBalance,
  getAutoEstimatedLoanBalance,
  getAutoEstimatedLoanRate,
} from "../../../../utils/autoEstimateLoan";
import useFormSync from "../hooks/useFormSync";
import ControlledField from "./ControlledField";
import EscrowTitleFields from "./EscrowTitleFields";
import DisclaimerModal from "../../popups/DisclaimerModal";
import EmailModal from "../../popups/EmailModal";
import TextMessageModal from "../../popups/TextMessageModal";
import useManualFields from "../hooks/useManualFields";
import ManualField from "./ManualField";
import { useModal } from "../../../../context/ModalContext";
import { CommonHeader } from "../../../../components/ui/CommonHeader";
import { buildBuyerPayload } from "../../../../utils/payloadBuilder";
import GeneralInformationSection from "../../components/GeneralInformationSection";
import { getPrepaidPaymentData } from "../utils/calculations";
import SimpleInput from "./SimpleInput";

export default function ManualSheetSection(props) {
  const {
    formData,
    setFormData,
    FloatingInput,
    FloatingNumber,
    CustomSelect,
    handleCalAmount,
    checkValidSalesPrice,
    isInitialLoad,
    showToaster,
    handleDownloadPrintPDF,
    handleClosingCostUpdate,
    ttbData,
    setUseMortgageBalance,
  } = props;

  const { user } = useAuth();
  const ucWords = (str = "") =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  const fullName = ucWords(
    `${user.first_name ?? ""} ${user.last_name ?? ""}`,
  ).trim();

  const buyerTemplateData = props.templateData ?? {};
  const buyerformData = props.buyerFormData ?? props.formData ?? {};

  const loanType = formData?.loanInfoMeta?.buyersLoanType || "Conventional";

  const { control, setValue, getValues } = useForm({
    defaultValues: { ...formData },
  });

  // ── Track which fields have pending blur updates so the mass sync effect
  //    can skip setValue for them.  A Set handles rapid multi-field tabs. ──
  const pendingBlurFieldsRef = useRef(new Set());

  const addPendingBlur = (fieldName) => {
    pendingBlurFieldsRef.current.add(fieldName);
  };

  useEffect(() => {
    if (!formData?.addressMeta) return;
    const addr = formData.addressMeta;
    [
      ["addressMeta.address", addr.address],
      ["addressMeta.city", addr.city],
      ["addressMeta.state", addr.state],
      ["addressMeta.zip", addr.zip],
      ["addressMeta.county", addr.county],
      ["addressMeta.closing_county", addr.closing_county],
      ["addressMeta.closing_state", addr.closing_state],
      ["addressMeta.stateid", addr.stateid],
      ["addressMeta.countyid", addr.countyid],
      ["addressMeta.companyid", addr.companyid],
      ["addressMeta.mrsid", addr.mrsid],
      ["addressMeta.unitNumber", addr.unitNumber],
      ["preparedBy", formData.preparedBy],
      ["preparedFor", formData.preparedFor],
    ].forEach(([name, val]) => {
      if (val !== undefined && val !== null) {
        setValue(name, val, { shouldDirty: false, shouldValidate: false });
      }
    });
  }, [
    formData?.addressMeta,
    formData?.preparedBy,
    formData?.preparedFor,
    setValue,
  ]);

  useFormSync({
    methods: { control, setValue, getValues },
    setFormData,
    handleCalAmount,
  });

  const [EscrowDisabled, setEscrowDisabled] = useState({
    escrowSettlement: true,
    ownersPolicy: true,
    lendersPolicy: true,
    premiumText: "",
  });

  const { openModal, closeModal } = useModal();

  // ── Throttle rapid Tab key presses to prevent update depth overflow ──
  useEffect(() => {
    let lastTabTime = 0;
    const TAB_THROTTLE_MS = 400; // adjust as needed (300-500ms usually works)

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;

      const now = Date.now();
      if (now - lastTabTime < TAB_THROTTLE_MS) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      lastTabTime = now;
    };

    // Capture phase ensures we intercept before React's handlers
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  // TEMP DEBUG — traces every commit of the Auto Estimate Loan fields, so
  // it's visible if anything downstream silently overwrites them (the same
  // class of bug found and fixed for the Origination Fee feature). Remove
  // once the feature is confirmed working end to end.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[autoEstimateLoan] settled state", {
      autoEstimateLoan: formData?.loanInfoMeta?.autoEstimateLoan,
      loansToBePaidBalance1: formData?.loanInfoMeta?.loansToBePaidBalance1,
      loansToBePaidRate1: formData?.loanInfoMeta?.loansToBePaidRate1,
    });
  }, [
    formData?.loanInfoMeta?.autoEstimateLoan,
    formData?.loanInfoMeta?.loansToBePaidBalance1,
    formData?.loanInfoMeta?.loansToBePaidRate1,
  ]);

  /* ── Convert origination-factor % → dollar cost ── */
  useEffect(() => {
    const metaList = formData?.buyerClosingCostsMeta;
    if (!Array.isArray(metaList) || !metaList.length) return;

    const salesPrice = parseCurrency(formData.salesPrice);

    let hasChanges = false;
    const updated = metaList.map((obj) => {
      if (!isOriginationRow(obj)) return obj;

      if (obj.isManual) {
        // User has taken control of this field — normalize the label only,
        // never touch value/total.
        const unit = (obj.unit || "").trim();
        if (unit !== "$" || obj.camelKey !== "originationFee") {
          hasChanges = true;
          return {
            ...obj,
            camelKey: "originationFee",
            key: "origination_fee",
            label: "Origination Fee",
            unit: "$",
          };
        }
        return obj;
      }

      if (!(salesPrice > 0)) return obj;

      // The original percentage never needs preserving on this row at all —
      // it already lives untouched in the loaded template data, which this
      // effect never mutates. Reading it from there instead of trying to
      // remember it on the row itself means a later Sales Price change
      // always has the real percentage to recompute from, and nothing
      // extra gets added to the row that then has to be sent to the
      // backend.
      const originalPercent = getTemplateOriginationPercent(
        buyerTemplateData?.conventional,
      );
      const formatted = calcOriginationDollarValue(originalPercent, salesPrice);

      const needsUpdate =
        obj.camelKey !== "originationFee" ||
        obj.unit !== "$" ||
        obj.value !== formatted ||
        obj.total !== formatted;

      if (!needsUpdate) return obj;

      hasChanges = true;
      return {
        ...obj,
        camelKey: "originationFee",
        key: "origination_fee",
        label: "Origination Fee",
        unit: "$",
        value: formatted,
        total: formatted,
        isManual: false,
      };
    });

    if (hasChanges) {
      setFormData((prev) => ({
        ...prev,
        phase: "buyerClosingCostsMeta",
        buyerClosingCostsMeta: updated,
      }));
    }
  }, [
    formData.salesPrice,
    formData?.buyerClosingCostsMeta,
    setFormData,
    buyerTemplateData,
  ]);

  useEffect(() => {
    if (user?.first_name || user?.last_name) {
      setFormData((prev) => ({
        ...prev,
        preparedBy: fullName,
      }));
      setValue("preparedBy", fullName);
    }
  }, [user, setFormData, setValue, fullName]);

  useEffect(() => {
    if (!formData?.prepaidsMeta) return;
    const result = {
      amount: parseCurrency(formData?.loanInfoMeta?.amount),
      adjusted: parseCurrency(formData?.loanInfoMeta?.adjusted),
    };
    const prepaidData = getPrepaidPaymentData(formData, result);

    const currentTaxes = formData?.prepaidsMeta?.taxesAmount;
    const currentIns = formData?.prepaidsMeta?.insAmount;
    const currentDays = formData?.prepaidsMeta?.daysInterestAmount;

    if (
      prepaidData.prepaidMonthTaxes === currentTaxes &&
      prepaidData.monthInsurance === currentIns &&
      prepaidData.daysInterestAmount === currentDays
    ) {
      return;
    }

    setFormData((prev) => {
      const updated = {
        ...prev,
        prepaidsMeta: {
          ...prev.prepaidsMeta,
          taxesAmount: prepaidData.prepaidMonthTaxes,
          insAmount: prepaidData.monthInsurance,
          daysInterestAmount: prepaidData.daysInterestAmount,
        },
        paymentMeta: {
          ...prev.paymentMeta,
          principalAndInterest: prepaidData.principalAndInterest,
          homeownerInsurance: prepaidData.homeOwnerInsuranceRes,
          realEstateTaxes: prepaidData.realEstateTaxes,
        },
      };
      const totals = computeTotals(updated);
      updated.totalClosingMeta = {
        ...updated.totalClosingMeta,
        ...totals,
      };
      return updated;
    });
  }, [
    formData?.prepaidsMeta?.taxesAmount,
    formData?.prepaidsMeta?.insAmount,
    formData?.prepaidsMeta?.daysInterestAmount,
  ]);

  useEffect(() => {
    if (isInitialLoad) return;
    const totals = computeTotals(formData);
    // loanInfoMeta.totalloansToBePaid/daysInterest are the SAME figures as
    // totals.existingTotal/existingLoanDaysInterest — computeTotals is the
    // one place that derives them (balance*rate/100 summed, prorated over
    // days), including paths that bypass the Manual Sheet's own field
    // handlers entirely (e.g. Auto Estimate Loan setting
    // loansToBePaidBalance1/Rate1 directly). Previously this effect only
    // mirrored the result into totalClosingMeta's display copy, so the
    // Manual Sheet's own "Total"/"Days Interest" fields could silently
    // drift out of sync with what Cash From/To Borrower was actually using.
    const nextTotalLoansToBePaid = totals.existingTotal.toFixed(2);
    const nextDaysInterest = totals.existingLoanDaysInterest.toFixed(2);
    setFormData((prev) => {
      const existing = prev?.totalClosingMeta || {};
      const existingLoanInfo = prev?.loanInfoMeta || {};
      const changed =
        totals.totalClosingCosts !== existing.totalClosingCosts ||
        totals.totalPrepaids !== existing.totalPrepaids ||
        totals.totalInvestment !== existing.totalInvestment ||
        totals.totalMonthlyPayment !== existing.totalMonthlyPayment ||
        nextTotalLoansToBePaid !==
          (existingLoanInfo.totalloansToBePaid ?? "0.00") ||
        nextDaysInterest !== (existingLoanInfo.daysInterest ?? "0.00");
      if (!changed) return prev;
      return {
        ...prev,
        phase: "totalClosingMeta",
        loanInfoMeta: {
          ...existingLoanInfo,
          totalloansToBePaid: nextTotalLoansToBePaid,
          daysInterest: nextDaysInterest,
        },
        totalClosingMeta: {
          ...existing,
          totalloansToBePaid: totals.existingTotal,
          totalClosingCosts: totals.totalClosingCosts,
          totalPrepaids: totals.totalPrepaids,
          totalMonthlyPayment: totals.totalMonthlyPayment,
          totalInvestment: totals.totalInvestment,
          totalInvestmentText: totals.totalInvestmentText,
        },
      };
    });
  }, [
    formData.buyerClosingCostsMeta,
    formData.closingCostsMeta,
    formData.titleSettlementMeta,
    formData.prepaidsMeta,
    formData.paymentMeta,
    // computeTotals also reads loansToBePaidBalance1/2/3, the matching
    // rates, days, amount/adjusted and buyersLoanType — all under
    // loanInfoMeta. Without this, editing "Loan to be Paid or Assumed"
    // never re-triggers this effect, so Cash From/To Borrower goes stale.
    formData.loanInfoMeta,
    isInitialLoad,
    setFormData,
  ]);

  useEffect(() => {
    if (!formData || Object.keys(formData).length === 0) return;

    const fields = {
      "addressMeta.address": formData.addressMeta?.address,
      "addressMeta.city": formData.addressMeta?.city,
      "addressMeta.state": formData.addressMeta?.state,
      "addressMeta.zip": formData.addressMeta?.zip,
      "addressMeta.county": formData.addressMeta?.county,
      "addressMeta.closing_county": formData.addressMeta?.closing_county,
      "addressMeta.closing_state": formData.addressMeta?.closing_state,
      "addressMeta.street_no": formData.addressMeta?.street_no,
      "addressMeta.street_name": formData.addressMeta?.street_name,
      "addressMeta.unitNumber": formData.addressMeta?.unitNumber,
      "addressMeta.stateid": formData.addressMeta?.stateid,
      "addressMeta.countyid": formData.addressMeta?.countyid,
      "addressMeta.companyid": formData.addressMeta?.companyid,
      "addressMeta.mrsid": formData.addressMeta?.mrsid,
      preparedBy: formData.preparedBy,
      preparedFor: formData.preparedFor,
      salesPrice: formData.salesPrice,
      "loanInfoMeta.buyersLoanType": formData.loanInfoMeta?.buyersLoanType,
      "loanInfoMeta.lendername": formData.loanInfoMeta?.lendername,
      "loanInfoMeta.conventionalPercent":
        formData.loanInfoMeta?.conventionalPercent,
      "loanInfoMeta.conventionalPercent2":
        formData.loanInfoMeta?.conventionalPercent2,
      "loanInfoMeta.interestRate1": formData.loanInfoMeta?.interestRate1,
      "loanInfoMeta.interestRate2": formData.loanInfoMeta?.interestRate2,
      "loanInfoMeta.termsInYears1": formData.loanInfoMeta?.termsInYears1,
      "loanInfoMeta.termsInYears2": formData.loanInfoMeta?.termsInYears2,
      "loanInfoMeta.amount": formData.loanInfoMeta?.amount,
      "loanInfoMeta.amount2": formData.loanInfoMeta?.amount2,
      "loanInfoMeta.adjusted": formData.loanInfoMeta?.adjusted,
      "loanInfoMeta.noPMICheck": formData.loanInfoMeta?.noPMICheck,
      "loanInfoMeta.showAPRCheck": formData.loanInfoMeta?.showAPRCheck,
      "loanInfoMeta.autoEstimateLoan": formData.loanInfoMeta?.autoEstimateLoan,
      "loanInfoMeta.loansToBePaidBalance1":
        formData.loanInfoMeta?.loansToBePaidBalance1,
      "loanInfoMeta.loansToBePaidRate1":
        formData.loanInfoMeta?.loansToBePaidRate1,
      "loanInfoMeta.loansToBePaidBalance2":
        formData.loanInfoMeta?.loansToBePaidBalance2,
      "loanInfoMeta.loansToBePaidRate2":
        formData.loanInfoMeta?.loansToBePaidRate2,
      "loanInfoMeta.loansToBePaidBalance3":
        formData.loanInfoMeta?.loansToBePaidBalance3,
      "loanInfoMeta.loansToBePaidRate3":
        formData.loanInfoMeta?.loansToBePaidRate3,
      "loanInfoMeta.totalloansToBePaid":
        formData.loanInfoMeta?.totalloansToBePaid,
      "loanInfoMeta.days": formData.loanInfoMeta?.days,
      "loanInfoMeta.daysInterest": formData.loanInfoMeta?.daysInterest,
      "loanInfoMeta.estimatedClosingDate":
        formData.loanInfoMeta?.estimatedClosingDate,
      "loanInfoMeta.annualPropertyTax":
        formData.loanInfoMeta?.annualPropertyTax,
      "prepaidsMeta.monthsTaxes": formData.prepaidsMeta?.monthsTaxes,
      "prepaidsMeta.rate": formData.prepaidsMeta?.rate,
      "prepaidsMeta.taxesAmount": formData.prepaidsMeta?.taxesAmount,
      "prepaidsMeta.monthsIns": formData.prepaidsMeta?.monthsIns,
      "prepaidsMeta.rateThou": formData.prepaidsMeta?.rateThou,
      "prepaidsMeta.insAmount": formData.prepaidsMeta?.insAmount,
      "prepaidsMeta.daysInterest": formData.prepaidsMeta?.daysInterest,
      "prepaidsMeta.daysInterestAmount":
        formData.prepaidsMeta?.daysInterestAmount,
      "prepaidsMeta.twoMonthsPMI": formData.prepaidsMeta?.twoMonthsPMI,
      "prepaidsMeta.fhaMip": formData.prepaidsMeta?.fhaMip,
      "prepaidsMeta.fhaMipCheck": formData.prepaidsMeta?.fhaMipCheck,
      "prepaidsMeta.fhaMipAmount": formData.prepaidsMeta?.fhaMipAmount,
      "prepaidsMeta.vaffFinance": formData.prepaidsMeta?.vaffFinance,
      "prepaidsMeta.vaffFinanceCheck": formData.prepaidsMeta?.vaffFinanceCheck,
      "prepaidsMeta.vaffFinanceRate": formData.prepaidsMeta?.vaffFinanceRate,
      "prepaidsMeta.vaffFinanceAmount":
        formData.prepaidsMeta?.vaffFinanceAmount,
      "prepaidsMeta.costs": formData.prepaidsMeta?.costs,
      "prepaidsMeta.costsAmount": formData.prepaidsMeta?.costsAmount,
      "paymentMeta.rateMonthlyPMI": formData.paymentMeta?.rateMonthlyPMI,
      "paymentMeta.monthlyExpense1": formData.paymentMeta?.monthlyExpense1,
      "paymentMeta.monthlyExpense1Amount":
        formData.paymentMeta?.monthlyExpense1Amount,
      "totalClosingMeta.totalMonthlyPayment":
        formData.totalClosingMeta?.totalMonthlyPayment,
    };

    // Capture pending fields for this cycle and reset for the next.
    const pending = pendingBlurFieldsRef.current;
    pendingBlurFieldsRef.current = new Set();

    Object.entries(fields).forEach(([name, value]) => {
      if (value === undefined || value === null) return;
      if (pending.has(name)) return; // skip fields that just blurred
      setValue(name, value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
  }, [formData, setValue]);

  useEffect(() => {
    console.log("formData", formData);
  }, [formData]);

  const closingCostsMetaRows = useManualFields(
    formData?.closingCostsMeta ?? [],
  );

  const cleanNumber = (v) => {
    if (v === undefined || v === null) return "";
    const s = typeof v === "string" ? v : String(v);
    return s.replace(/[^0-9.]/g, "");
  };

  const parseCurrency = (val) => {
    const n = Number(String(val ?? "").replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  // Payoff of the existing loan(s) being refinanced: each loan's own
  // accrued-interest share (balance * rate / 100), summed, then prorated
  // over `days` at a 360-day year to get the per-diem interest owed since
  // the last payment — added on top of the raw balance total to get the
  // true final payoff figure (not just the sum of balances).
  const calculateTotalLoansToBePaid = (loanInfoMeta) => {
    const balance1 = parseCurrency(loanInfoMeta?.loansToBePaidBalance1 || 0);
    const balance2 = parseCurrency(loanInfoMeta?.loansToBePaidBalance2 || 0);
    const balance3 = parseCurrency(loanInfoMeta?.loansToBePaidBalance3 || 0);
    const rate1 = parseCurrency(loanInfoMeta?.loansToBePaidRate1 || 0);
    const rate2 = parseCurrency(loanInfoMeta?.loansToBePaidRate2 || 0);
    const rate3 = parseCurrency(loanInfoMeta?.loansToBePaidRate3 || 0);
    const days = parseCurrency(loanInfoMeta?.days || 0);

    const existingRateTotal =
      (balance1 * rate1) / 100 +
      (balance2 * rate2) / 100 +
      (balance3 * rate3) / 100;

    const totalBalance = balance1 + balance2 + balance3;

    // Truncated (not rounded) — per-diem interest is conventionally quoted
    // this way rather than rounded up.
    const daysInterest =
      Math.floor(((existingRateTotal / 360) * days) * 100) / 100;

    const totalloansToBePaid = totalBalance + daysInterest;

    return {
      totalloansToBePaid: totalloansToBePaid.toFixed(2),
      daysInterest: daysInterest.toFixed(2),
    };
  };

  const handleEscrowTitleFields = (fieldName, formatted) => {
    addPendingBlur(`titleSettlementMeta.${fieldName}`);
    setFormData((prev) => ({
      ...prev,
      phase: "titleSettlementMeta",
      titleSettlementMeta: {
        ...prev.titleSettlementMeta,
        [fieldName]: formatted,
      },
    }));
    setValue(`titleSettlementMeta.${fieldName}`, formatted);
  };

  const handleContinue = (setting) => {
    const pstString = new Date().toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
    });
    const [pstDate, pstTimeFull] = pstString.split(", ");
    const pstTime =
      pstTimeFull?.split(":").slice(0, 2).join(":") +
      (pstTimeFull?.includes("AM") ? " AM" : " PM");

    let premiumText = `*Premium changed ${fullName} ${pstDate} ${pstTime} PST`;
    setEscrowDisabled((prev) => ({
      ...prev,
      ...setting,
      premiumText: premiumText,
    }));

    if (setting.escrowSettlement === false) {
      setFormData((prev) => ({
        ...prev,
        phase: "escrowSettlementMeta",
        titleSettlementMeta: {
          ...prev.titleSettlementMeta,
          escrowSettlement: false,
          premiumText: premiumText,
        },
      }));
    }
    if (setting.ownersPolicy === false) {
      setFormData((prev) => ({
        ...prev,
        phase: "ownersPolicyMeta",
        titleSettlementMeta: {
          ...prev.titleSettlementMeta,
          ownersPolicy: false,
          premiumText: premiumText,
        },
      }));
    }
    if (setting.lendersPolicy === false) {
      setFormData((prev) => ({
        ...prev,
        phase: "lendersPolicyMeta",
        titleSettlementMeta: {
          ...prev.titleSettlementMeta,
          lendersPolicy: false,
          premiumText: premiumText,
        },
      }));
    }
    setFormData((prev) => ({
      ...prev,
      phase: "escrowPremiumChanged",
      escrowPremiumChanged: true,
    }));
    closeModal();
  };

  return (
    <>
      <div className="model-body-calc-pg">
        <div className="calculator-page application-fe">
          <CommonHeader title={`Refinance Net Sheet`}>
            <div className="ch-btnsave">
              <button
                type="button"
                value="New"
                className="ch-btnsave-new"
                onClick={() => props.onCloseManual()}
              >
                New
              </button>
              {!isGuestUser(user) && (
                <button
                  type="button"
                  value="Save"
                  className="ch-btnsave-save"
                  onClick={() => props.handleSaveCalc()}
                >
                  Save
                </button>
              )}
            </div>
          </CommonHeader>

          <div className="row-three-wrap">
            <div className="calc-column-a">
              <GeneralInformationSection
                formData={formData}
                control={control}
                getValues={getValues}
                mode="buyer"
                setValue={setValue}
                user={user}
                openModal={openModal}
                closeModal={closeModal}
                showToaster={showToaster}
                setCompanyId={props.setCompanyId}
                onAddressEnriched={props.onAddressEnriched}
              />

              <h3 className="m-top-24 m-btm-16">Loan Info (Required)</h3>
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="salesPrice"
                    control={control}
                    InputComponent={FloatingNumber}
                    label="Estimated Appraised Value"
                    placeholder="0.00"
                    type="text"
                    className="input-filled"
                    defaultValue={formData.salesPrice || ""}
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        phase: "salesPrice",
                        salesPrice: formatted,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <span className="heading-c">Loan Information</span>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <label className="lbl-section">Loan Type</label>
                  <Controller
                    name="loanInfoMeta.buyersLoanType"
                    control={control}
                    defaultValue={
                      formData?.loanInfoMeta?.buyersLoanType ?? "Conventional"
                    }
                    render={({ field }) => (
                      <CustomSelect
                        {...field}
                        name="loanInfoMeta.buyersLoanType"
                        placeholder={field.value || "Select Loan Type"}
                        options={["FHA 203b", "VA", "Conventional"]}
                        onChange={(value) => {
                          field.onChange(value);
                          setFormData((prev) => {
                            const prevLoan = prev?.loanInfoMeta ?? {};
                            let selectedMeta =
                              buyerTemplateData.conventional || [];
                            switch (value) {
                              case "FHA 203b":
                                selectedMeta = buyerTemplateData.fha || [];
                                break;
                              case "VA":
                                selectedMeta = buyerTemplateData.va || [];
                                break;
                              default:
                                selectedMeta =
                                  buyerTemplateData.conventional || [];
                            }
                            return {
                              ...prev,
                              phase: "buyerClosingCostsMeta 12",
                              loanInfoMeta: {
                                ...prevLoan,
                                buyersLoanType: value,
                              },
                              buyerClosingCostsMeta: selectedMeta,
                            };
                          });
                        }}
                      />
                    )}
                  />
                </div>

                <div className="calc-frm-fld form-group">
                  <Controller
                    name="loanInfoMeta.lendername"
                    control={control}
                    defaultValue={formData?.loanInfoMeta?.lendername ?? ""}
                    render={({ field }) => (
                      <FloatingInput
                        {...field}
                        placeholder="Name Of Lenders"
                        type="text"
                        onBlur={(e) => {
                          addPendingBlur("loanInfoMeta.lendername");
                          setFormData((p) => ({
                            ...p,
                            loanInfoMeta: {
                              ...p.loanInfoMeta,
                              lendername: e.target.value,
                            },
                          }));
                        }}
                      />
                    )}
                  />
                  <label htmlFor="input-lendername">Name Of Lenders</label>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <Controller
                    name="loanInfoMeta.actualAnnualTax"
                    control={control}
                    defaultValue={
                      formData?.loanInfoMeta?.actualAnnualTax ?? "0.00"
                    }
                    render={({ field }) => (
                      <FloatingInput
                        {...field}
                        placeholder="0.00"
                        type="text"
                        onBlur={(e) => {
                          addPendingBlur("loanInfoMeta.actualAnnualTax");
                          setFormData((p) => ({
                            ...p,
                            loanInfoMeta: {
                              ...p.loanInfoMeta,
                              actualAnnualTax: e.target.value,
                            },
                          }));
                        }}
                      />
                    )}
                  />
                  <label htmlFor="input-actualAnnualTax">
                    Actual Annual Tax
                  </label>
                </div>

                <div className="calc-frm-fld form-group sign-of-amount">
                  <Controller
                    name="loanInfoMeta.actualAnnualIns"
                    control={control}
                    defaultValue={
                      formData?.loanInfoMeta?.actualAnnualIns ?? "0.00"
                    }
                    render={({ field }) => (
                      <FloatingInput
                        {...field}
                        placeholder="0.00"
                        type="text"
                        onBlur={(e) => {
                          addPendingBlur("loanInfoMeta.actualAnnualIns");
                          setFormData((p) => ({
                            ...p,
                            loanInfoMeta: {
                              ...p.loanInfoMeta,
                              actualAnnualIns: e.target.value,
                            },
                          }));
                        }}
                      />
                    )}
                  />
                  <label htmlFor="input-actualAnnualIns">
                    Actual Annual Ins
                  </label>
                </div>
              </div>

              <div className="calc-frm-field field-baseline">
                <div className="calc-frm-field-division">
                  {loanType === "Conventional" && (
                    <>
                      <div className="calc-frm-fld form-group">
                        <span>
                          <b>1st Loan</b>
                        </span>
                      </div>
                      <div className="calc-frm-fld form-group">
                        <FloatingNumber
                          type="text"
                          name="loanInfoMeta.conventionalPercent"
                          value={
                            formData.loanInfoMeta?.conventionalPercent || ""
                          }
                          control={control}
                          placeholder="0.00"
                          className="inpt-pla"
                          onChange={(e) => { }}
                          onBlur={(e) => {
                            addPendingBlur("loanInfoMeta.conventionalPercent");
                            setFormData((p) => ({
                              ...p,
                              loanInfoMeta: {
                                ...p.loanInfoMeta,
                                conventionalPercent: e.target.value,
                              },
                            }));
                          }}
                        />
                        <label>Loan to Value %</label>
                      </div>
                    </>
                  )}

                  <div className="calc-frm-fld form-group">
                    <FloatingNumber
                      type="text"
                      name="loanInfoMeta.interestRate1"
                      control={control}
                      placeholder="0.00"
                      decimals={4}
                      className="inpt-pla"
                      value={formData.loanInfoMeta?.interestRate1 || ""}
                      onChange={(e) => { }}
                      onBlur={(e) => {
                        addPendingBlur("loanInfoMeta.interestRate1");
                        setFormData((p) => ({
                          ...p,
                          loanInfoMeta: {
                            ...p.loanInfoMeta,
                            interestRate1: e.target.value,
                          },
                        }));
                      }}
                    />
                    <label>Interest Rate %</label>
                  </div>

                  <div className="calc-frm-fld form-group">
                    <FloatingNumber
                      type="text"
                      name="loanInfoMeta.termsInYears1"
                      control={control}
                      placeholder="0"
                      decimals={0}
                      className="inpt-pla"
                      value={formData.loanInfoMeta?.termsInYears1 || ""}
                      onChange={(e) => { }}
                      onBlur={(e) => {
                        addPendingBlur("loanInfoMeta.termsInYears1");
                        setFormData((p) => ({
                          ...p,
                          loanInfoMeta: {
                            ...p.loanInfoMeta,
                            termsInYears1: e.target.value,
                          },
                        }));
                      }}
                    />
                    <label>Terms in Years</label>
                  </div>
                </div>

                <div className="calc-frm-field-division">
                  {loanType === "Conventional" && (
                    <>
                      <div className="calc-frm-fld form-group">
                        <span>
                          <b>2nd Loan</b>
                        </span>
                      </div>
                      <div className="calc-frm-fld form-group">
                        <FloatingNumber
                          type="text"
                          name="loanInfoMeta.conventionalPercent2"
                          value={
                            formData.loanInfoMeta?.conventionalPercent2 || ""
                          }
                          control={control}
                          placeholder="0.00"
                          className="inpt-pla"
                          onChange={(e) => { }}
                          onBlur={(e) => {
                            addPendingBlur("loanInfoMeta.conventionalPercent2");
                            setFormData((p) => ({
                              ...p,
                              loanInfoMeta: {
                                ...p.loanInfoMeta,
                                conventionalPercent2: e.target.value,
                              },
                            }));
                          }}
                        />
                        <label>Loan to Value %</label>
                      </div>
                    </>
                  )}

                  <div className="calc-frm-fld form-group">
                    <FloatingNumber
                      type="text"
                      name="loanInfoMeta.interestRate2"
                      control={control}
                      placeholder="0.00"
                      className="inpt-pla"
                      value={formData.loanInfoMeta?.interestRate2 || ""}
                      onChange={(e) => { }}
                      onBlur={(e) => {
                        addPendingBlur("loanInfoMeta.interestRate2");
                        setFormData((p) => ({
                          ...p,
                          loanInfoMeta: {
                            ...p.loanInfoMeta,
                            interestRate2: e.target.value,
                          },
                        }));
                      }}
                    />
                    <label>Interest Rate %</label>
                  </div>

                  <div className="calc-frm-fld form-group">
                    <FloatingNumber
                      type="text"
                      name="loanInfoMeta.termsInYears2"
                      control={control}
                      placeholder="0"
                      decimals={0}
                      className="inpt-pla"
                      value={formData.loanInfoMeta?.termsInYears2 || ""}
                      onChange={(e) => { }}
                      onBlur={(e) => {
                        addPendingBlur("loanInfoMeta.termsInYears2");
                        setFormData((p) => ({
                          ...p,
                          loanInfoMeta: {
                            ...p.loanInfoMeta,
                            termsInYears2: e.target.value,
                          },
                        }));
                      }}
                    />
                    <label>Terms in Years</label>
                  </div>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.amount"
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    value={formData.loanInfoMeta?.amount || ""}
                    onChange={(e) => { }}
                    onBlur={(e) => {
                      addPendingBlur("loanInfoMeta.amount");
                      setFormData((p) => ({
                        ...p,
                        loanInfoMeta: {
                          ...p.loanInfoMeta,
                          amount: e.target.value,
                        },
                      }));
                    }}
                  />
                  <label>New Loan Amount</label>
                </div>
                <div className="calc-frm-fld form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.amount2"
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    value={formData.loanInfoMeta?.amount2 || ""}
                    onChange={(e) => { }}
                    onBlur={(e) => {
                      addPendingBlur("loanInfoMeta.amount2");
                      setFormData((p) => ({
                        ...p,
                        loanInfoMeta: {
                          ...p.loanInfoMeta,
                          amount2: e.target.value,
                        },
                      }));
                    }}
                  />
                  <label>New Loan Amount</label>
                </div>
              </div>

              {(loanType === "FHA 203b" || loanType === "VA") && (
                <>
                  <div className="calc-frm-field">
                    <div className="calc-frm-fld form-group sign-of-amount">
                      <FloatingNumber
                        type="text"
                        name="loanInfoMeta.adjusted"
                        control={control}
                        placeholder="0.00"
                        className="inpt-pla"
                        value={formData.loanInfoMeta?.adjusted || ""}
                        onChange={(e) => { }}
                        onBlur={(e) => {
                          addPendingBlur("loanInfoMeta.adjusted");
                          setFormData((p) => ({
                            ...p,
                            loanInfoMeta: {
                              ...p.loanInfoMeta,
                              adjusted: e.target.value,
                            },
                          }));
                        }}
                      />
                      <label>Adjusted</label>
                    </div>
                  </div>
                </>
              )}

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  {loanType === "Conventional" && (
                    <div className="floating-input-wrapper">
                      <Controller
                        name="loanInfoMeta.noPMICheck"
                        control={control}
                        defaultValue={!!formData?.loanInfoMeta?.noPMICheck}
                        render={({ field }) => (
                          <p>
                            <input
                              type="checkbox"
                              checked={!!field.value}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                field.onChange(checked);
                                setFormData((p) => ({
                                  ...p,
                                  loanInfoMeta: {
                                    ...(p?.loanInfoMeta || {}),
                                    noPMICheck: checked,
                                  },
                                }));
                              }}
                            />{" "}
                            No PMI
                          </p>
                        )}
                      />
                    </div>
                  )}
                </div>
                <div className="calc-frm-fld form-group">
                  <div className="floating-input-wrapper">
                    <Controller
                      name="loanInfoMeta.showAPRCheck"
                      control={control}
                      defaultValue={!!formData?.loanInfoMeta?.showAPRCheck}
                      render={({ field }) => (
                        <p>
                          <input
                            type="checkbox"
                            checked={!!field.value}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              field.onChange(checked);
                              setFormData((p) => ({
                                ...p,
                                loanInfoMeta: {
                                  ...(p?.loanInfoMeta || {}),
                                  showAPRCheck: checked,
                                },
                              }));
                            }}
                          />{" "}
                          Show APR
                        </p>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <span className="heading-c">Loan to be Paid or Assumed</span>
                  <div className="switch-wrap">
                    <div className="switch-wrap-a">
                      <Controller
                        name="loanInfoMeta.autoEstimateLoan"
                        control={control}
                        render={({ field }) => (
                          <label className="switch">
                            <input
                              type="checkbox"
                              id="toggle"
                              checked={!!field.value}
                              onChange={(e) => {
                                try {
                                  const checked = e.target.checked;
                                  field.onChange(checked);
                                  setFormData((p) => {
                                    const nextLoanInfo = {
                                      ...(p?.loanInfoMeta || {}),
                                      autoEstimateLoan: checked,
                                    };
                                    if (
                                      checked &&
                                      hasAutoEstimatableLoanBalance(ttbData)
                                    ) {
                                      // Same source as Seller's "Loan to be
                                      // Paid or Assumed" toggle: pull the
                                      // existing balance/rate this
                                      // address's title-records lookup
                                      // found, instead of requiring the
                                      // user to type them in.
                                      nextLoanInfo.loansToBePaidBalance1 =
                                        getAutoEstimatedLoanBalance(ttbData);
                                      nextLoanInfo.loansToBePaidRate1 =
                                        getAutoEstimatedLoanRate(ttbData);
                                    } else if (!checked) {
                                      // Fall back to whatever the user
                                      // manually entered for the mortgage
                                      // amount, same as Seller.
                                      nextLoanInfo.loansToBePaidBalance1 =
                                        p?.loanInfoMeta?.mortgageAmount ||
                                        "0.00";
                                    }
                                    // eslint-disable-next-line no-console
                                    console.log(
                                      "[autoEstimateLoan] toggle onChange",
                                      {
                                        checked,
                                        rawLoanBalance: ttbData?.loanBalance,
                                        hasBalance:
                                          hasAutoEstimatableLoanBalance(
                                            ttbData,
                                          ),
                                        mortgageAmountFallback:
                                          p?.loanInfoMeta?.mortgageAmount,
                                        nextBalance:
                                          nextLoanInfo.loansToBePaidBalance1,
                                        nextRate:
                                          nextLoanInfo.loansToBePaidRate1,
                                      },
                                    );
                                    return {
                                      ...p,
                                      loanInfoMeta: nextLoanInfo,
                                    };
                                  });
                                  setUseMortgageBalance?.(checked);
                                } catch (err) {
                                  console.warn(
                                    "Failed toggling autoEstimateLoan sync",
                                    err,
                                  );
                                }
                              }}
                            />
                            <span className="slider"></span>
                          </label>
                        )}
                      />
                    </div>
                    <div className="switch-wrap-b">
                      <p className="tooltip">
                        Auto Estimate Loan
                        <div className="question-tooltip">
                          <ul>
                            <li>
                              <b>Balance</b> is derived from transaction
                              history.
                            </li>
                            <li>
                              <b>Rate</b> is based on loan rates at the time of
                              origination.
                            </li>
                            <li>
                              This is only an estimate and may be different
                              based on the borrower's repayment history.
                            </li>
                          </ul>
                        </div>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <span className="heading-c">Balance $</span>
                </div>
                <div className="calc-frm-fld form-group">
                  <span className="heading-c">Rate %</span>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name={"loanInfoMeta.loansToBePaidBalance1"}
                    control={control}
                    className={"input-filled"}
                    InputComponent={FloatingNumber}
                    defaultValue={
                      formData?.loanInfoMeta?.loansToBePaidBalance1 ?? "0.00"
                    }
                    placeholder="0.00"
                    label={"Existing 1st"}
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => {
                        const updatedLoanInfoMeta = {
                          ...(p.loanInfoMeta ?? {}),
                          loansToBePaidBalance1: formatted,
                        };
                        const { totalloansToBePaid, daysInterest } =
                          calculateTotalLoansToBePaid(updatedLoanInfoMeta);
                        updatedLoanInfoMeta.totalloansToBePaid =
                          totalloansToBePaid;
                        updatedLoanInfoMeta.daysInterest = daysInterest;
                        return {
                          ...p,
                          loanInfoMeta: updatedLoanInfoMeta,
                        };
                      });
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-percentage">
                  <ControlledField
                    name={"loanInfoMeta.loansToBePaidRate1"}
                    control={control}
                    className={"input-filled"}
                    InputComponent={FloatingNumber}
                    defaultValue={
                      formData?.loanInfoMeta?.loansToBePaidRate1 ?? "0.00"
                    }
                    placeholder="0.00"
                    label={"Existing 1st"}
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => {
                        const updatedLoanInfoMeta = {
                          ...(p.loanInfoMeta ?? {}),
                          loansToBePaidRate1: formatted,
                        };
                        const { totalloansToBePaid, daysInterest } =
                          calculateTotalLoansToBePaid(updatedLoanInfoMeta);
                        updatedLoanInfoMeta.totalloansToBePaid =
                          totalloansToBePaid;
                        updatedLoanInfoMeta.daysInterest = daysInterest;
                        return {
                          ...p,
                          loanInfoMeta: updatedLoanInfoMeta,
                        };
                      });
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name={"loanInfoMeta.loansToBePaidBalance2"}
                    control={control}
                    className={"input-filled"}
                    InputComponent={FloatingNumber}
                    defaultValue={
                      formData?.loanInfoMeta?.loansToBePaidBalance2 ?? "0.00"
                    }
                    placeholder="0.00"
                    label={"Existing 2nd"}
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => {
                        const updatedLoanInfoMeta = {
                          ...(p.loanInfoMeta ?? {}),
                          loansToBePaidBalance2: formatted,
                        };
                        const { totalloansToBePaid, daysInterest } =
                          calculateTotalLoansToBePaid(updatedLoanInfoMeta);
                        updatedLoanInfoMeta.totalloansToBePaid =
                          totalloansToBePaid;
                        updatedLoanInfoMeta.daysInterest = daysInterest;
                        return {
                          ...p,
                          loanInfoMeta: updatedLoanInfoMeta,
                        };
                      });
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-percentage">
                  <ControlledField
                    name={"loanInfoMeta.loansToBePaidRate2"}
                    control={control}
                    className={"input-filled"}
                    InputComponent={FloatingNumber}
                    defaultValue={
                      formData?.loanInfoMeta?.loansToBePaidRate2 ?? "0.00"
                    }
                    placeholder="0.00"
                    label={"Existing 2nd"}
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => {
                        const updatedLoanInfoMeta = {
                          ...(p.loanInfoMeta ?? {}),
                          loansToBePaidRate2: formatted,
                        };
                        const { totalloansToBePaid, daysInterest } =
                          calculateTotalLoansToBePaid(updatedLoanInfoMeta);
                        updatedLoanInfoMeta.totalloansToBePaid =
                          totalloansToBePaid;
                        updatedLoanInfoMeta.daysInterest = daysInterest;
                        return {
                          ...p,
                          loanInfoMeta: updatedLoanInfoMeta,
                        };
                      });
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name={"loanInfoMeta.loansToBePaidBalance3"}
                    control={control}
                    className={"input-filled"}
                    InputComponent={FloatingNumber}
                    defaultValue={
                      formData?.loanInfoMeta?.loansToBePaidBalance3 ?? "0.00"
                    }
                    placeholder="0.00"
                    label={"Existing 3rd"}
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => {
                        const updatedLoanInfoMeta = {
                          ...(p.loanInfoMeta ?? {}),
                          loansToBePaidBalance3: formatted,
                        };
                        const { totalloansToBePaid, daysInterest } =
                          calculateTotalLoansToBePaid(updatedLoanInfoMeta);
                        updatedLoanInfoMeta.totalloansToBePaid =
                          totalloansToBePaid;
                        updatedLoanInfoMeta.daysInterest = daysInterest;
                        return {
                          ...p,
                          loanInfoMeta: updatedLoanInfoMeta,
                        };
                      });
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-percentage">
                  <ControlledField
                    name={"loanInfoMeta.loansToBePaidRate3"}
                    control={control}
                    className={"input-filled"}
                    InputComponent={FloatingNumber}
                    defaultValue={
                      formData?.loanInfoMeta?.loansToBePaidRate3 ?? "0.00"
                    }
                    placeholder="0.00"
                    label={"Existing 3rd"}
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => {
                        const updatedLoanInfoMeta = {
                          ...(p.loanInfoMeta ?? {}),
                          loansToBePaidRate3: formatted,
                        };
                        const { totalloansToBePaid, daysInterest } =
                          calculateTotalLoansToBePaid(updatedLoanInfoMeta);
                        updatedLoanInfoMeta.totalloansToBePaid =
                          totalloansToBePaid;
                        updatedLoanInfoMeta.daysInterest = daysInterest;
                        return {
                          ...p,
                          loanInfoMeta: updatedLoanInfoMeta,
                        };
                      });
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <ControlledField
                    name={"loanInfoMeta.days"}
                    control={control}
                    className={"input-filled"}
                    InputComponent={FloatingNumber}
                    defaultValue={formData?.loanInfoMeta?.days ?? "0"}
                    placeholder="0"
                    label={"Days"}
                    type="text"
                    decimals={0}
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => {
                        const updatedLoanInfoMeta = {
                          ...(p.loanInfoMeta ?? {}),
                          days: formatted,
                        };
                        const { totalloansToBePaid, daysInterest } =
                          calculateTotalLoansToBePaid(updatedLoanInfoMeta);
                        updatedLoanInfoMeta.totalloansToBePaid =
                          totalloansToBePaid;
                        updatedLoanInfoMeta.daysInterest = daysInterest;
                        return {
                          ...p,
                          loanInfoMeta: updatedLoanInfoMeta,
                        };
                      });
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name={"loanInfoMeta.daysInterest"}
                    control={control}
                    className={"input-filled"}
                    InputComponent={FloatingNumber}
                    defaultValue={
                      formData?.loanInfoMeta?.daysInterest ?? "0.00"
                    }
                    placeholder="0.00"
                    label={"Days Interest"}
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      // A manual override here replaces the computed
                      // daysInterest outright (not re-derived from
                      // rate/balance/days) but still has to roll forward
                      // into totalloansToBePaid, same as every other input
                      // to this payoff figure.
                      setFormData((p) => {
                        const balance1 = parseCurrency(
                          p.loanInfoMeta?.loansToBePaidBalance1 || 0,
                        );
                        const balance2 = parseCurrency(
                          p.loanInfoMeta?.loansToBePaidBalance2 || 0,
                        );
                        const balance3 = parseCurrency(
                          p.loanInfoMeta?.loansToBePaidBalance3 || 0,
                        );
                        const totalBalance = balance1 + balance2 + balance3;
                        const manualDaysInterest = parseCurrency(formatted);
                        return {
                          ...p,
                          loanInfoMeta: {
                            ...(p.loanInfoMeta ?? {}),
                            daysInterest: formatted,
                            totalloansToBePaid: (
                              totalBalance + manualDaysInterest
                            ).toFixed(2),
                          },
                        };
                      });
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name={"loanInfoMeta.totalloansToBePaid"}
                    control={control}
                    className={"input-filled"}
                    InputComponent={FloatingNumber}
                    defaultValue={
                      formData?.loanInfoMeta?.totalloansToBePaid ?? "0.00"
                    }
                    placeholder="0.00"
                    label={"Total"}
                    type="text"
                    cleanNumber={cleanNumber}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <Controller
                    name="loanInfoMeta.estimatedClosingDate"
                    control={control}
                    defaultValue={
                      formData?.loanInfoMeta?.estimatedClosingDate ?? ""
                    }
                    render={({ field }) => (
                      <FloatingInput
                        type="date"
                        name="loanInfoMeta.estimatedClosingDate"
                        label=""
                        placeholder="MM/DD/YYYY"
                        className="inpt-pla"
                        value={
                          formData?.loanInfoMeta?.estimatedClosingDate ?? ""
                        }
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            loanInfoMeta: {
                              ...(p?.loanInfoMeta ?? {}),
                              estimatedClosingDate: e.target.value,
                            },
                          }))
                        }
                      />
                    )}
                  />
                  <label htmlFor="input-amount1">Estimated Closing Date</label>
                </div>
              </div>
            </div>

            <div className="calc-column-b">
              <h3 className="m-btm-16">Closing Costs</h3>

              <EscrowTitleFields
                control={control}
                formData={formData}
                isInitialLoad={isInitialLoad}
                openModal={openModal}
                closeModal={closeModal}
                FloatingNumber={FloatingNumber}
                cleanNumber={cleanNumber}
                setFormData={setFormData}
                setValue={setValue}
                onFormatted={handleEscrowTitleFields}
                EscrowDisabled={EscrowDisabled}
                onContinue={handleContinue}
              />

              <div
                key={formData?.loanInfoMeta?.buyersLoanType || "Conventional"}
                className="calc-frm-field"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gridRowGap: "0",
                }}
              >
                {(formData?.buyerClosingCostsMeta ?? []).map((item, index) => (
                  <div
                    // Not item?.camelKey: the Origination row's camelKey
                    // itself changes ("originationFactor" -> "originationFee")
                    // the moment it's converted, which would make React
                    // remount this field instead of just updating it. This
                    // list's order/length is stable within a session (it's
                    // always the same template-derived array), so the index
                    // is a safe, stable key here.
                    key={index}
                    className="calc-frm-fld form-group sign-of-amount"
                  >
                    <SimpleInput
                      value={item?.value != null ? String(item.value) : "0.00"}
                      label={item?.label ?? ""}
                      onChange={(e) => {
                        const raw = cleanNumber(e.target.value);
                        setFormData((prev) => ({
                          ...prev,
                          buyerClosingCostsMeta: (
                            prev.buyerClosingCostsMeta ?? []
                          ).map((it) =>
                            it.camelKey === item.camelKey
                              ? { ...it, value: raw }
                              : it,
                          ),
                        }));
                      }}
                      onBlur={(e) => {
                        const formatted = utilFormatCurrency(
                          cleanNumber(e.target.value),
                        );
                        addPendingBlur(`buyerClosingCostsMeta.${index}.value`);
                        setFormData((prev) => ({
                          ...prev,
                          buyerClosingCostsMeta: (
                            prev.buyerClosingCostsMeta ?? []
                          ).map((it) =>
                            it.camelKey === item.camelKey
                              ? {
                                  ...it,
                                  value: formatted,
                                  isManual: isDeliberateManualEdit(it),
                                }
                              : it,
                          ),
                        }));
                      }}
                    />
                  </div>
                ))}
              </div>

              {formData?.escrowPremiumChanged && (
                <p className="premium-txt">{EscrowDisabled.premiumText}</p>
              )}

              <div className="table-calc-frm">
                <div className="table-calc-frm-inner">
                  <div className="calc-frm-field m-top-24">
                    <div className="calc-frm-fld form-group clmn-a-width">
                      <span className="heading-c">Cost</span>
                    </div>
                    <div className="calc-frm-fld form-group clmn-b-width">
                      <span className="heading-c">Type</span>
                    </div>
                    <div className="calc-frm-fld form-group clmn-c-width">
                      <span className="heading-c">Rate</span>
                    </div>
                    <div className="calc-frm-fld form-group fld-txt-right clmn-d-width">
                      <span className="heading-c">Total</span>
                    </div>
                  </div>

                  {closingCostsMetaRows.map((row, index) => (
                    <ManualField
                      key={row._meta?.camel || index}
                      row={row}
                      FloatingInput={FloatingInput}
                      FloatingNumber={FloatingNumber}
                      CustomSelect={CustomSelect}
                      cleanNumber={cleanNumber}
                      onFieldUpdate={handleClosingCostUpdate}
                    />
                  ))}
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="total-amunt-fld">
                  <div className="amount-txt heading-c">
                    Total Closing Costs
                  </div>
                  <div className="amount-number heading-c">
                    ${" "}
                    {formatCurrency(
                      formData?.totalClosingMeta?.totalClosingCosts ?? 0,
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="calc-column-c">
              <h3 className="m-btm-16">Prepaids</h3>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <label className="lbl-section">Months Taxes</label>
                  <ControlledField
                    name="prepaidsMeta.monthsTaxes"
                    defaultValue={formData?.prepaidsMeta?.monthsTaxes ?? ""}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0"
                    type="text"
                    decimals={0}
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          monthsTaxes: formatted,
                        },
                      }));
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-percentage">
                  <label className="lbl-section">Rate</label>
                  <ControlledField
                    name="prepaidsMeta.rate"
                    defaultValue={formData?.prepaidsMeta?.rate ?? ""}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    inputProps={{ decimals: 4 }}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          rate: formatted,
                        },
                      }));
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="prepaidsMeta.taxesAmount"
                    defaultValue={formData?.prepaidsMeta?.taxesAmount ?? ""}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          taxesAmount: formatted,
                        },
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <label className="lbl-section">Months Ins.</label>
                  <ControlledField
                    name="prepaidsMeta.monthsIns"
                    defaultValue={formData?.prepaidsMeta?.monthsIns ?? ""}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0"
                    type="text"
                    decimals={0}
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          monthsIns: formatted,
                        },
                      }));
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-percentage">
                  <label className="lbl-section">Rate/Thou</label>
                  <ControlledField
                    name="prepaidsMeta.rateThou"
                    defaultValue={formData?.prepaidsMeta?.rateThou ?? ""}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    inputProps={{ decimals: 4 }}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          rateThou: formatted,
                        },
                      }));
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="prepaidsMeta.insAmount"
                    defaultValue={formData?.prepaidsMeta?.insAmount ?? ""}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          insAmount: formatted,
                        },
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <label className="lbl-section">Days Interest</label>
                  <ControlledField
                    name="prepaidsMeta.daysInterest"
                    defaultValue={formData?.prepaidsMeta?.daysInterest ?? ""}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0"
                    type="text"
                    decimals={0}
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          daysInterest: formatted,
                        },
                      }));
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group"></div>
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="prepaidsMeta.daysInterestAmount"
                    defaultValue={
                      formData?.prepaidsMeta?.daysInterestAmount ?? ""
                    }
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          daysInterestAmount: formatted,
                        },
                      }));
                    }}
                  />
                </div>
              </div>

              {(() => {
                const loanType =
                  formData?.loanInfoMeta?.buyersLoanType || "Conventional";

                switch (loanType) {
                  case "Conventional":
                    return (
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group"></div>
                        <div className="calc-frm-fld form-group"></div>
                        <div className="calc-frm-fld form-group sign-of-amount">
                          <ControlledField
                            name="prepaidsMeta.twoMonthsPMI"
                            control={control}
                            InputComponent={FloatingNumber}
                            label="Amount"
                            placeholder="0.00"
                            className="inpt-pla"
                            type="text"
                            defaultValue={
                              formData?.prepaidsMeta?.twoMonthsPMI ?? ""
                            }
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) => {
                              addPendingBlur(name);
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  twoMonthsPMI: formatted,
                                },
                              }));
                            }}
                          />
                          <label htmlFor="input-amount1">2 Months PMI </label>
                        </div>
                      </div>
                    );

                  case "FHA 203b":
                    return (
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group">
                          <ControlledField
                            name="prepaidsMeta.fhaMip"
                            control={control}
                            InputComponent={FloatingNumber}
                            label=""
                            placeholder=""
                            type="text"
                            defaultValue={formData?.prepaidsMeta?.fhaMip ?? ""}
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) => {
                              addPendingBlur(name);
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  fhaMip: formatted,
                                },
                              }));
                            }}
                          />
                          <label htmlFor="input-conventionalPercent">
                            FHA MIP Finance
                          </label>
                        </div>

                        <div className="calc-frm-fld form-group">
                          <Controller
                            name="prepaidsMeta.fhaMipCheck"
                            control={control}
                            defaultValue={!!formData?.prepaidsMeta?.fhaMipCheck}
                            render={({ field }) => (
                              <p className="txt-center">
                                <input
                                  type="checkbox"
                                  checked={!!field.value}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    field.onChange(checked);
                                    setFormData((p) => ({
                                      ...p,
                                      prepaidsMeta: {
                                        ...(p?.prepaidsMeta || {}),
                                        fhaMipCheck: checked,
                                      },
                                    }));
                                  }}
                                />{" "}
                              </p>
                            )}
                          />
                        </div>

                        <div className="calc-frm-fld form-group sign-of-amount">
                          <ControlledField
                            name="prepaidsMeta.fhaMipAmount"
                            control={control}
                            InputComponent={FloatingNumber}
                            label="Amount"
                            placeholder="0.00"
                            className="inpt-pla"
                            type="text"
                            defaultValue={
                              formData?.prepaidsMeta?.fhaMipAmount ?? ""
                            }
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) => {
                              addPendingBlur(name);
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  fhaMipAmount: formatted,
                                },
                              }));
                            }}
                          />
                        </div>
                      </div>
                    );

                  case "VA":
                    return (
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group">
                          <ControlledField
                            name="prepaidsMeta.vaffFinance"
                            control={control}
                            InputComponent={FloatingInput}
                            label=""
                            placeholder=""
                            type="text"
                            defaultValue={
                              formData?.prepaidsMeta?.vaffFinance ?? ""
                            }
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) => {
                              addPendingBlur(name);
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  vaffFinance: formatted,
                                },
                              }));
                            }}
                          />
                          <label htmlFor="input-conventionalPercent">
                            VA FF Finance
                          </label>
                        </div>

                        <div className="calc-frm-fld form-group small-input-check">
                          <Controller
                            name="prepaidsMeta.vaffFinanceCheck"
                            control={control}
                            defaultValue={
                              !!formData?.prepaidsMeta?.vaffFinanceCheck
                            }
                            render={({ field }) => (
                              <p>
                                <input
                                  type="checkbox"
                                  checked={!!field.value}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    field.onChange(checked);
                                    setFormData((p) => ({
                                      ...p,
                                      prepaidsMeta: {
                                        ...(p?.prepaidsMeta || {}),
                                        vaffFinanceCheck: checked,
                                      },
                                    }));
                                  }}
                                />{" "}
                              </p>
                            )}
                          />
                        </div>
                        <div className="calc-frm-fld form-group small-input-two">
                          <ControlledField
                            name="prepaidsMeta.vaffFinanceRate"
                            control={control}
                            InputComponent={FloatingInput}
                            label=""
                            placeholder=""
                            type="text"
                            defaultValue={
                              formData?.prepaidsMeta?.vaffFinanceRate ?? ""
                            }
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) => {
                              addPendingBlur(name);
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  vaffFinanceRate: formatted,
                                },
                              }));
                            }}
                          />
                          <label htmlFor="input-conventionalPercent"></label>
                        </div>
                        <div className="calc-frm-fld form-group sign-of-amount">
                          <ControlledField
                            name="prepaidsMeta.vaffFinanceAmount"
                            control={control}
                            InputComponent={FloatingNumber}
                            label=""
                            placeholder="0.00"
                            className="inpt-pla"
                            type="text"
                            defaultValue={
                              formData?.prepaidsMeta?.vaffFinanceAmount ?? ""
                            }
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) => {
                              addPendingBlur(name);
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  vaffFinanceAmount: formatted,
                                },
                              }));
                            }}
                          />
                        </div>
                      </div>
                    );

                  default:
                    return null;
                }
              })()}

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <ControlledField
                    name="prepaidsMeta.costs"
                    control={control}
                    InputComponent={FloatingInput}
                    label=""
                    placeholder=""
                    type="text"
                    defaultValue={formData?.prepaidsMeta?.costs ?? ""}
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          costs: formatted,
                        },
                      }));
                    }}
                  />
                  <label htmlFor="input-conventionalPercent">Costs</label>
                </div>

                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="prepaidsMeta.costsAmount"
                    control={control}
                    InputComponent={FloatingNumber}
                    label="Amount"
                    placeholder="0.00"
                    className="inpt-pla"
                    type="text"
                    defaultValue={formData?.prepaidsMeta?.costsAmount ?? ""}
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) => {
                      addPendingBlur(name);
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          costsAmount: formatted,
                        },
                      }));
                    }}
                  />
                  <label htmlFor="input-amount1">Amount</label>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="prepaidsMeta.totalPrepaidItems"
                    label="Total Prepaid Items"
                    value={formData?.totalClosingMeta?.totalPrepaids ?? "0.00"}
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    disabled
                  />
                </div>
              </div>

              <div className="box-wrap layout-box">
                <div className="box-layout-head">
                  <h3>Payment</h3>
                </div>
                <div className="wrap-middle-container">
                  {[
                    {
                      label: "Principal and Interest",
                      key: "principalAndInterest",
                    },
                    { label: "Real Estate Taxes", key: "realEstateTaxes" },
                    { label: "Homeowner Insurance", key: "homeownerInsurance" },
                  ].map((item) => (
                    <div className="totl-estimate pay-btm-space" key={item.key}>
                      <div className="totl-estimate-txt heading-c">
                        {item.label} $
                      </div>
                      <div className="totl-estimate-amnt heading-c">
                        {formatCurrency(formData?.paymentMeta?.[item.key]) ??
                          "0.00"}
                      </div>
                    </div>
                  ))}

                  <div className="totl-estimate pay-btm-space">
                    <div className="totl-estimate-txt heading-c txt-w-input">
                      <div className="calc-frm-fld form-group">
                        <ControlledField
                          name="paymentMeta.rateMonthlyPMI"
                          control={control}
                          InputComponent={FloatingNumber}
                          defaultValue={
                            formData?.paymentMeta?.rateMonthlyPMI ?? ""
                          }
                          placeholder="0.00"
                          type="text"
                          cleanNumber={cleanNumber}
                          onFormatted={(name, formatted) => {
                            addPendingBlur(name);
                            setFormData((p) => ({
                              ...p,
                              paymentMeta: {
                                ...(p?.paymentMeta ?? {}),
                                rateMonthlyPMI: formatted,
                              },
                            }));
                          }}
                        />
                      </div>
                      Rate Monthly
                      {loanType === "Conventional" && <> PMI</>}
                      {loanType === "USDA" && <> MI</>}
                      {loanType === "FHA 203b" && <> MMI</>} $
                    </div>
                    <div className="totl-estimate-amnt heading-c">
                      {formData?.paymentMeta?.rateMonthlyPMIAmount ?? "0.00"}
                    </div>
                  </div>
                  <div className="box-layout-head">
                    <h4>Monthly Expenses</h4>
                  </div>

                  {[1].map((idx) => (
                    <div className="calc-frm-field pd-top-16" key={idx}>
                      <div className="calc-frm-fld form-group">
                        <ControlledField
                          name={`paymentMeta.monthlyExpense${idx}`}
                          control={control}
                          value={
                            formData?.paymentMeta?.[`monthlyExpense${idx}`] ??
                            ""
                          }
                          defaultValue={
                            formData?.paymentMeta?.[`monthlyExpense${idx}`] ??
                            ""
                          }
                          InputComponent={FloatingInput}
                          placeholder=""
                          type="text"
                          cleanNumber={cleanNumber}
                          onFormatted={(name, formatted) => {
                            addPendingBlur(name);
                            setFormData((p) => ({
                              ...p,
                              paymentMeta: {
                                ...(p?.paymentMeta ?? {}),
                                [`monthlyExpense${idx}`]: formatted,
                              },
                            }));
                          }}
                        />
                        <label>Monthly Expense</label>
                      </div>

                      <div className="calc-frm-fld form-group sign-of-amount">
                        <FloatingNumber
                          type="text"
                          name={`paymentMeta.monthlyExpense${idx}Amount`}
                          control={control}
                          placeholder="0.00"
                          className="inpt-pla"
                          value={
                            formData?.paymentMeta?.[
                            `monthlyExpense${idx}Amount`
                            ] ?? ""
                          }
                          defaultValue={
                            formData?.paymentMeta?.[
                            `monthlyExpense${idx}Amount`
                            ] ?? ""
                          }
                          onBlur={(e) => {
                            addPendingBlur(
                              `paymentMeta.monthlyExpense${idx}Amount`,
                            );
                            setFormData((p) => ({
                              ...p,
                              paymentMeta: {
                                ...(p?.paymentMeta ?? {}),
                                [`monthlyExpense${idx}Amount`]: e.target.value,
                              },
                            }));
                          }}
                        />
                        <label>Amount</label>
                      </div>
                    </div>
                  ))}

                  <div className="box-layout-head">
                    <h4>Total</h4>
                  </div>

                  <div className="calc-frm-field pd-top-16">
                    <div className="calc-frm-fld form-group sign-of-amount">
                      <ControlledField
                        name="totalClosingMeta.totalMonthlyPayment"
                        control={control}
                        value={
                          formData?.totalClosingMeta?.totalMonthlyPayment ?? ""
                        }
                        defaultValue={
                          formData?.totalClosingMeta?.totalMonthlyPayment ?? ""
                        }
                        InputComponent={FloatingNumber}
                        placeholder=""
                        label="Total Monthly Payment"
                        type="text"
                        cleanNumber={cleanNumber}
                        onFormatted={(name, formatted) => {
                          addPendingBlur(name);
                          setFormData((p) => ({
                            ...p,
                            totalClosingMeta: {
                              ...(p?.totalClosingMeta ?? {}),
                              totalMonthlyPayment: formatted,
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="totl-estimate box-ly">
                    <div className="totl-estimate-txt heading-c">
                      {formData?.totalClosingMeta?.totalInvestmentText} $
                    </div>
                    <div
                      className={`totl-estimate-amnt heading-c ${(formData?.totalClosingMeta?.totalInvestment ?? 0) < 0
                          ? "text-red-600"
                          : ""
                        }`}
                    >
                      {formatCurrency(
                        formData?.totalClosingMeta?.totalInvestment ?? "0.00",
                      )}
                    </div>
                  </div>
                </div>

                <div className="box-action">
                  <ul>
                    <li>
                      <button
                        className="icon-pdf"
                        onClick={(e) => {
                          const isValid = checkValidSalesPrice();
                          if (!isValid) return;
                          handleDownloadPrintPDF("download", buyerformData);
                        }}
                      >
                        Save PDF
                      </button>
                    </li>
                    <li>
                      <button
                        className="icon-print"
                        onClick={(e) => {
                          const isValid = checkValidSalesPrice();
                          if (!isValid) return;
                          handleDownloadPrintPDF("print", buyerformData);
                        }}
                      >
                        Print
                      </button>
                    </li>
                    <li>
                      <button
                        className="icon-email"
                        onClick={(e) => {
                          const isValid = checkValidSalesPrice();
                          if (!isValid) return;
                          e.preventDefault();
                          const updated = buildBuyerPayload({
                            buyerformData,
                            user,
                          });
                          openModal({
                            title: "Email a Closing Cost Estimate",
                            size: "mediumsizewidth",
                            content: (
                              <EmailModal
                                onClose={closeModal}
                                type="buyer"
                                showToaster={showToaster}
                                buyerFormData={updated}
                              />
                            ),
                          });
                        }}
                      >
                        Email
                      </button>
                    </li>
                    <li>
                      <button
                        className="icon-text"
                        onClick={(e) => {
                          const isValid = checkValidSalesPrice();
                          if (!isValid) return;
                          e.preventDefault();
                          const updated = buildBuyerPayload({
                            buyerformData: formData,
                            user,
                          });
                          openModal({
                            title: "Text Message to Customer",
                            size: "mediumsizewidth",
                            content: (
                              <TextMessageModal
                                onClose={closeModal}
                                type="buyer"
                                showToaster={showToaster}
                                buyerFormData={updated}
                              />
                            ),
                          });
                        }}
                      >
                        Text
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="calc-frm-field">
                <a
                  href=""
                  className="icon-info"
                  onClick={(e) => {
                    e.preventDefault();
                    openModal({
                      title: "Disclaimer",
                      size: "mediumsizewidth",
                      content: (
                        <DisclaimerModal calc="buyer" onClose={closeModal} />
                      ),
                    });
                  }}
                >
                  Disclaimer
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
